import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Lock, Eye, Trash2 } from "lucide-react";

export default function SensitiveReveal({ onReveal, onDeleted }) {
  const [revealOpen, setRevealOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const doReveal = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/profile/documents/reveal", { password });
      onReveal(data.revealed || {});
      setRevealed(true);
      setRevealOpen(false);
      setPassword("");
      toast.success("Sensitive fields revealed — this access was logged");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete("/profile/documents");
      toast.success("All sensitive document data deleted");
      setDeleteOpen(false);
      setRevealed(false);
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8 rounded-card border border-hairline bg-surface px-5 py-4 flex flex-wrap items-center gap-4" data-testid="pii-bar">
      <div className="flex items-start gap-3 flex-1 min-w-[240px]">
        <Lock size={15} className="text-dusk mt-0.5 shrink-0" />
        <p className="text-sm text-sand-600">
          Passport numbers, date of birth, medical notes and similar fields are{" "}
          <span className="font-medium text-ink">encrypted at rest and masked below</span>. Revealing them
          requires your password, and every access is logged.
          {revealed && <span className="text-ember font-medium"> Revealed on this page until you leave.</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" className="btn-swiss btn-outline text-xs flex items-center gap-1.5"
          onClick={() => setRevealOpen(true)} data-testid="pii-reveal-btn">
          <Eye size={13} /> Reveal
        </button>
        <button type="button" className="btn-swiss btn-outline text-xs flex items-center gap-1.5 text-error"
          onClick={() => setDeleteOpen(true)} data-testid="pii-delete-btn">
          <Trash2 size={13} /> Delete sensitive data
        </button>
      </div>

      <Dialog open={revealOpen} onOpenChange={(o) => { setRevealOpen(o); if (!o) setPassword(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm your password</DialogTitle>
            <DialogDescription>
              Sensitive fields are decrypted only after a fresh password check. This reveal is recorded in your audit log.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={doReveal} className="space-y-4">
            <input type="password" autoFocus className="swiss-input w-full" placeholder="Your password"
              value={password} onChange={(e) => setPassword(e.target.value)} data-testid="pii-password-input" />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-swiss btn-outline text-xs" onClick={() => setRevealOpen(false)}>Cancel</button>
              <button type="submit" className="btn-swiss btn-primary text-xs" disabled={busy || !password} data-testid="pii-reveal-submit">
                {busy ? "Verifying…" : "Reveal fields"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete all sensitive data?</DialogTitle>
            <DialogDescription>
              This permanently erases every encrypted document value — passport numbers, DOB, trusted traveler numbers,
              medical notes and emergency contacts. Expiry dates and other preferences stay. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-swiss btn-outline text-xs" onClick={() => setDeleteOpen(false)}>Cancel</button>
            <button type="button" className="btn-swiss text-xs bg-error text-white" disabled={busy}
              onClick={doDelete} data-testid="pii-delete-confirm">
              {busy ? "Deleting…" : "Delete everything"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
